import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../../components/ui';
import { Package, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

interface SorteoDetalle {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  total_vendido: string;
  total_comision: string;
  total_premios: string;
}

interface PaqueteDetalle {
  paquete_id: string;
  paquete_nombre: string;
  total_vendido: string;
  total_comision: string;
  total_premios: string;
  sorteos: SorteoDetalle[];
}

const fmtCRC = (n: number) =>
  n.toLocaleString('es-CR', { style: 'currency', currency: 'CRC', minimumFractionDigits: 0 });

export const BanqueroResumenPage: React.FC = () => {
  const [paquetes, setPaquetes] = useState<PaqueteDetalle[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await fetchAPI<PaqueteDetalle[]>('/banquero/cuenta/detalle-paquetes');
      setPaquetes(data);
      if (data.length <= 5) {
        setExpandidos(new Set(data.map(p => p.paquete_id)));
      }
    } catch {
      setError('Error al cargar el resumen. Intente de nuevo.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleGrupo = (id: string) => {
    setExpandidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saldoGlobal = paquetes.reduce((acc, p) => {
    const vendido = Number(p.total_vendido);
    const comision = Number(p.total_comision);
    const premios = Number(p.total_premios);
    return acc + (vendido - comision - premios);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resumen por Paquete</h1>
          <p className="text-sm text-gray-500 mt-1">
            Totales por paquete de reglas. Solo sorteos cerrados con número ganador verificado.
          </p>
        </div>
        <button
          onClick={cargar}
          disabled={cargando}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={cargando ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Resumen global */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Paquetes</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{paquetes.length}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo total</p>
          <p className={`text-2xl font-bold mt-1 ${
            saldoGlobal > 0 ? 'text-emerald-600' : saldoGlobal < 0 ? 'text-red-600' : 'text-gray-900'
          }`}>
            {fmtCRC(saldoGlobal)}
          </p>
        </Card>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {cargando && paquetes.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : paquetes.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          No hay paquetes con actividad en esta banca.
        </Card>
      ) : (
        <div className="space-y-3">
          {paquetes.map(paquete => {
            const abierto = expandidos.has(paquete.paquete_id);
            const vendido = Number(paquete.total_vendido);
            const premios = Number(paquete.total_premios);
            const comision = Number(paquete.total_comision);
            const saldo = vendido - comision - premios;

            return (
              <Card key={paquete.paquete_id} className="overflow-hidden">
                {/* Encabezado del paquete */}
                <button
                  onClick={() => toggleGrupo(paquete.paquete_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                >
                  {abierto ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                  <Package size={18} className="text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900">{paquete.paquete_nombre}</span>
                    <span className="ml-2 text-xs text-gray-400">
                      {paquete.sorteos.length} sorteo{paquete.sorteos.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className={`text-sm font-semibold ${
                    saldo > 0 ? 'text-emerald-600' : saldo < 0 ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {fmtCRC(saldo)}
                  </span>
                </button>

                {abierto && (
                  <div className="border-t border-gray-100">
                    {/* Totales del paquete */}
                    <div className="bg-gray-50 border-b border-gray-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-gray-500 text-xs uppercase">
                            <th className="px-4 py-2 text-left font-medium">Sorteo</th>
                            <th className="px-4 py-2 text-right font-medium">Vendido</th>
                            <th className="px-4 py-2 text-right font-medium">Premios</th>
                            <th className="px-4 py-2 text-right font-medium">Comisión</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {paquete.sorteos.map(sorteo => {
                            const sVendido = Number(sorteo.total_vendido);
                            const sPremios = Number(sorteo.total_premios);
                            const sComision = Number(sorteo.total_comision);
                            return (
                              <tr key={sorteo.sorteo_id} className="bg-white hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-700 text-sm">
                                  {sorteo.sorteo_nombre || sorteo.sorteo_id}
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-blue-600">
                                  {fmtCRC(sVendido)}
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-red-600">
                                  {fmtCRC(sPremios)}
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-yellow-700">
                                  {fmtCRC(sComision)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {/* Fila de totales del paquete */}
                        <tfoot>
                          <tr className="bg-gray-100 font-semibold text-sm border-t border-gray-200">
                            <td className="px-4 py-2 text-gray-600">Total paquete</td>
                            <td className="px-4 py-2 text-right font-mono text-blue-700">
                              {fmtCRC(vendido)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-red-700">
                              {fmtCRC(premios)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-yellow-800">
                              {fmtCRC(comision)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BanqueroResumenPage;
